import { useState } from "react";
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Typography,
} from "@mui/material";
import { Company } from "models/Company";
import EditOutlined from "@mui/icons-material/EditOutlined";
import { useGetOfficesQuery } from "app/api/companies";
import LoadingDisplay from "components/LoadingDisplay";
import EmptyMessage from "components/EmptyMessage";
import OfficeForm from "../components/OfficeForm";
import Add from "@mui/icons-material/Add";
import DataLabel from "components/DataLabel";
import { useAuthContext } from "features/auth/AuthContext";
import { UserRole } from "features/auth/types/user";

type OfficesSectionProps = { company: Company };

const OfficesSection: React.FC<OfficesSectionProps> = ({ company }) => {
  const { hasRole } = useAuthContext();
  const canEditCompanies = hasRole([UserRole.ADMIN, UserRole.USER_ADMIN]);

  const [showAddOfficeDialog, setShowAddOfficeDialog] = useState(false);
  const [editOfficeId, setEditOfficeId] = useState<string | null>(null);

  const { data: offices, isLoading: isLoadingOffices } = useGetOfficesQuery({
    companyId: company.id,
  });

  return (
    <>
      <DialogContent sx={{ p: 1 }}>
        {isLoadingOffices ? (
          <LoadingDisplay message="Hämtar kontor..." />
        ) : (
          <List disablePadding>
            {!offices?.length && (
              <ListItem divider>
                <ListItemText primary={<EmptyMessage>Inga kontor hittades</EmptyMessage>} />
              </ListItem>
            )}
            {offices?.map((office) =>
              office.id === editOfficeId ? (
                <ListItem divider sx={{ justifyContent: "center" }} key={office.id}>
                  <OfficeForm
                    company={company}
                    officeToEdit={office}
                    onClose={() => setEditOfficeId(null)}
                  />
                </ListItem>
              ) : (
                <ListItem
                  key={office.id}
                  divider
                  secondaryAction={
                    <IconButton
                      hidden={!canEditCompanies}
                      onClick={() => {
                        setShowAddOfficeDialog(false);
                        setEditOfficeId(office.id);
                      }}
                    >
                      <EditOutlined />
                    </IconButton>
                  }
                >
                  <ListItemText
                    primary={office.address}
                    slotProps={{ primary: { fontWeight: "bold" } }}
                    secondary={
                      <>
                        <Typography>
                          {office.postalCode} {office.city}
                        </Typography>
                        <DataLabel label="Timpris">
                          {office.hourlyRate ? `${office.hourlyRate} kr/h` : "-"}
                        </DataLabel>
                      </>
                    }
                  />
                </ListItem>
              )
            )}
          </List>
        )}
      </DialogContent>

      {!showAddOfficeDialog && canEditCompanies && (
        <DialogActions>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => {
              setEditOfficeId(null);
              setShowAddOfficeDialog(true);
            }}
            sx={{ ml: "auto" }}
            disabled={isLoadingOffices}
          >
            Nytt kontor
          </Button>
        </DialogActions>
      )}

      {showAddOfficeDialog && (
        <Box display="flex" justifyContent="center" p={1}>
          <OfficeForm company={company} onClose={() => setShowAddOfficeDialog(false)} />
        </Box>
      )}
    </>
  );
};

export default OfficesSection;
